import React from 'react';
import {
    NumberInput,
    Edit,
    TextInput,
    required,
    ArrayInput,
    SelectInput,
    SimpleFormIterator,
    TabbedForm,
    FormTab,
    DateTimeInput,
    Toolbar,
    SaveButton,
    DeleteButton,
    useRefresh,
    TabbedFormTabs,
} from 'react-admin';
import CallMissedIcon from '@mui/icons-material/CallMissed';
import AddIcon from '@mui/icons-material/Add';
import DoneIcon from '@mui/icons-material/Done';
import InfoIcon from '@mui/icons-material/Info';
import {formatCurrency, request, getChoices, languageCheckAndReload } from "../../util";
import {translate} from "../../i18n/customI18nProvider";
import config from '../../config';
import Button  from '@material-ui/core/Button';

import { validatePositiveNumber } from '../../validators/number_validators';
import Chip from '@material-ui/core/Chip';
import LinkIcon from '@mui/icons-material/Link';
import Grid from '@material-ui/core/Grid';
import BlockIcon from '@mui/icons-material/Block';
import WarningIcon from '@mui/icons-material/Warning';
import Swal from 'sweetalert2';
import ClientAddEntrance from '../../components/client/AddEntrance';
import { AttachMoney, ConfirmationNumber, MoneyOff } from '@mui/icons-material';
import SmartMenuButton from '../../components/SmartMenuButton';
import { TransactionsList } from './TransactionsList';
import { addMoney, addUnpaidMoney, forceRefundMoney, refundClient, refundMoneyByCash } from '../../services/client.service';
import { Box, IconButton } from '@material-ui/core';

const PostTitle = ({ record }) => {
    return <span>{translate('custom.client')} {record ? `"${record.name ? record.name : record.short_id}"` : ''}</span>;
};

const OldUidsList = ({ record }) => {
    let old_uids = record.old_uids;
    if(old_uids && old_uids.length)
        return (
            <>
                <h3>{translate('custom.old_uids')}</h3>
                {
                    old_uids.map((old_uids) => (
                        <Chip label={old_uids} style={{margin: 1}} />
                    ))
                }
            </>
        );
    else
        return (
            <></>
        );
}

function formRefundPage(uid) {
    window.open('/refund/' + uid, '_blank')
}

const QuickActions = (props) => {
    const record = props.record;
    const isSmall = props.isSmall ? props.isSmall : false;
    const currency = props.currency ?? config.currency;
    const refresh = useRefresh();
    const [open, setOpen] = React.useState(false);
    const addActions = [
        {
            label: translate('custom.add_entrance'),
            onClick: async () => {
                setOpen(true);
            },
            icon: <ConfirmationNumber />
        },
        {
            label: translate('custom.add_money'),
            onClick: async () => {
                await addMoney(record.uid, currency)
                refresh();
            },
            icon: <AttachMoney />
        },
        {
            label: translate('custom.add_unpaid_money'),
            onClick: async () => {
                await addUnpaidMoney(record.uid, currency)
                refresh();
            },
            icon: <MoneyOff />
        }
    ]

    const refundActions = [
        {
            label: translate('custom.form_refund_button'),
            onClick: async () => {
                formRefundPage(record.uid)
            },
            icon: <LinkIcon />
        },
        {
            label: translate('custom.refund_all_twint'),
            onClick: async () => {
                await refundClient(record.uid, config.refund_methods.TWINT)
                refresh();
            },
            icon: <CallMissedIcon />
        },
        {
            label: translate('custom.refund_all_mypos'),
            onClick: async () => {
                await refundClient(record.uid, config.refund_methods.MYPOS)
                refresh();
            },
            icon: <CallMissedIcon />
        },
        {
            label: translate('custom.refund_by_cash'),
            onClick: async () => {
                await refundMoneyByCash(record.uid)
                refresh();
            },
            icon: <AttachMoney />
        },
        {
            label: translate('custom.force_refund'),
            onClick: async () => {
                await forceRefundMoney(record.uid)
                refresh();
            },
            icon: <AttachMoney />
        }
    ]

    return (
        <>
            <Box 
                display="grid" 
                width="100%"
                gridTemplateColumns="repeat(12, 1fr)"
                gridRowGap={6}
                gridColumnGap={12}
            >
                <Box gridColumn={{ xs: 'span 12', sm: 'span 6', md: 'span 4', lg: 'span 3' }}>
                    <SmartMenuButton
                        width={"100%"}
                        items={addActions}
                        size={'small'}
                        variant={'outlined'}
                        color={'success'}
                        label={translate('custom.add_transaction')}
                        icon={<AddIcon />}
                    />
                </Box>
                <Box gridColumn={{ xs: 'span 12', sm: 'span 6', md: 'span 4', lg: 'span 3' }}>
                    <SmartMenuButton
                        width={"100%"}
                        items={refundActions}
                        size={'small'}
                        color={'error'}
                        label={translate('custom.refund')}
                        icon={<CallMissedIcon />}
                    />
                </Box>
            </Box>
            <div style={{backgroundColor: 'red'}}>
                <ClientAddEntrance record={record} refresh={refresh} open={open} onClose={() => setOpen(false)} />
            </div>
        </>
    )
}

const CustomToolbar = props => {

    const refresh = useRefresh();
    const isSmall = props.isSmall ? props.isSmall : false;
    let currentDate = new Date();
    currentDate = currentDate.toISOString();

    return (
        <Toolbar {...props}>
            <SaveButton {...props}
                disabled={props.pristine || props.invalid || props.saving || props.validating}
                style={{marginRight: 'auto'}}
                onSuccess={() => {
                    refresh();
                }}
            />
            <DeleteButton mutationMode="pessimistic" size="medium" />
        </Toolbar>
    )
};

function showDetails(event_id, short_id, archived=false) {
    if(archived)
        window.open('/event/' + event_id + '/client/' + short_id, "_blank") //to open new page with event as it is archived
    else
        window.open('/client/' + short_id, "_blank") //to open new page without event as it is not archived
}

const ClientDetailsView = (props) => {

    const { record } = props;
    const uid = record["uid"];
    const event_id = record["event_id"];
    let currentDate = new Date();
    currentDate = currentDate.toISOString();
    const pans = [];
    for (const transaction of record.transactions) {
        if(transaction.pan) {
            let panTxt = transaction.pan;
            if(panTxt.length == 5) {
                // Format *1234 to 1234
                panTxt = panTxt.substring(1);
            }
            pans.push(panTxt);
        }
    }

    return (
        <div>
            
            <Grid container direction="column">
                <Grid item xs={12} sm={12} md={12} lg={12}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Button style={{textTransform: 'none', fontSize: '20px', color :'black'}} onClick={() => showDetails(event_id, uid, record?.archived)} >
                            {translate('custom.balance')} : {formatCurrency(record.balance)}
                            <InfoIcon style={{marginLeft: '10px'}} />
                        </Button>
                        <Chip label={translate(record.is_bracelet_used ? 'custom.bracelet_used' : 'custom.bracelet_not_used')} color={record.is_bracelet_used ? "primary" : "default"} />
                    </div>
                </Grid>
            </Grid>

            <Grid container direction="column">
                <Grid item xs={12} sm={12} md={12} lg={12}>
                    <Button style={{textTransform: 'none', fontSize: '15px', color :'black'}}
                        onClick={() => {
                            navigator.clipboard?.writeText(record.uid)

                            Swal.fire({
                                toast: true,
                                position: 'top',
                                icon: 'success',
                                title: translate('custom.copied'),
                                showConfirmButton: false,
                                timer: 2000
                            });

                        }}
                    >
                        {translate('custom.uid')} : {record.uid}
                    </Button>
                    <Button style={{textTransform: 'none', fontSize: '15px', color :'black'}}
                        onClick={() => {
                            navigator.clipboard?.writeText(record.short_id)

                            Swal.fire({
                                toast: true,
                                position: 'top',
                                icon: 'success',
                                title: translate('custom.copied'),
                                showConfirmButton: false,
                                timer: 2000
                            });

                        }}>
                        {translate('custom.short_id')} : {record.short_id}
                    </Button>
                </Grid>
            </Grid>
            
            {(pans.length > 0) && (
                <Grid container direction="column" xs={12} sm={12} md={12} lg={12}>
                    <Grid item xs={12} sm={12} md={12} lg={12}>
                        <div style={{display: 'flex', flexDirection: 'row', flexWrap: 'wrap', alignItems: 'center', gap: '5px'}}>
                           <p style={{paddingLeft: '8px', fontFamily: 'Helvetica'}}>
                                {translate('custom.pans_used_by_client')}:
                            </p>
                            {pans.map((pan, index) => (
                                <Chip style={{fontWeight: 'bold', fontSize: '14px'}} key={index} label={`# ${pan}`} color="primary" size="small"
                                    onClick={() => {
                                        navigator?.clipboard?.writeText(pan)

                                        Swal.fire({
                                            toast: true,
                                            position: 'top',
                                            icon: 'success',
                                            title: translate('custom.copied'),
                                            showConfirmButton: false,
                                            timer: 2000
                                        });

                                    }}
                                />
                            ))}
                        </div>
                    </Grid>
                </Grid>
            )}
        </div>
    )

}

export class ClientShow extends React.Component {

    constructor(props) {
        super(props);
        this.state = {
            vouchers : [],
            currency: props.options.currency,
            currencyCode: props.options.currencyCode,
            currencySymbol: props.options.currencySymbol
        };
    }

    async componentDidMount() {

        let general_information = await request('/events/general', 'GET', null);
        if(general_information.error) {
            alert(general_information.error);
            return;
        }
        languageCheckAndReload(general_information.language);

        this.setState({
            currency: general_information.currencySymbol ? general_information.currencySymbol : this.state.currency,
            currencyCode: general_information.currencyCode ? general_information.currencyCode : this.state.currencyCode,
            currencySymbol: general_information.currencySymbol ? general_information.currencySymbol : this.state.currencySymbol
        });

        let vocs = [];
        let vouchers = await request('/vouchers', 'GET', null)
        if (vouchers.error) {
            alert(vouchers.error);
            return;
        }
        if(vouchers) {
            for(let i=0; i<vouchers.length; i++) {
                vocs.push({
                    id: vouchers[i]._id,
                    name: vouchers[i].name
                });
            }
        }
        if(vocs.length)
            vocs.sort((a, b) => a.name > b.name);
        this.setState({
            vouchers: vocs
        });
    }

    validateVoucherSelect = async (values) => {
        
        const errors = {};
        
        let vouchers = values.vouchers;
        
        errors.vouchers = [];
        
        let vouchers_length = 0;
        if(vouchers) {
            vouchers_length = vouchers.length;
        }
        for(let i=0; i<vouchers_length; i++) {
            errors.vouchers.push({});
        }
        for(let i=vouchers_length-1; i>=0; i--) {
            for (let j=0; j<i; j++) {
                if (vouchers && vouchers[i] && vouchers[j] && (vouchers[i]["voucher_id"] === vouchers[j]["voucher_id"])) {
                    errors.vouchers[i]['voucher_id'] = translate('custom.same_voucher_not_alowed');
                }
            }
        }
        
        return errors
    }

    render() {

        let isSmall = false;
        if(/(android|bb\d+|meego).+mobile|avantgo|bada\/|blackberry|blazer|compal|elaine|fennec|hiptop|iemobile|ip(hone|od)|ipad|iris|kindle|Android|Silk|lge |maemo|midp|mmp|netfront|opera m(ob|in)i|palm( os)?|phone|p(ixi|re)\/|plucker|pocket|psp|series(4|6)0|symbian|treo|up\.(browser|link)|vodafone|wap|windows (ce|phone)|xda|xiino/i.test(navigator.userAgent) 
            || /1207|6310|6590|3gso|4thp|50[1-6]i|770s|802s|a wa|abac|ac(er|oo|s-)|ai(ko|rn)|al(av|ca|co)|amoi|an(ex|ny|yw)|aptu|ar(ch|go)|as(te|us)|attw|au(di|-m|r |s )|avan|be(ck|ll|nq)|bi(lb|rd)|bl(ac|az)|br(e|v)w|bumb|bw-(n|u)|c55\/|capi|ccwa|cdm-|cell|chtm|cldc|cmd-|co(mp|nd)|craw|da(it|ll|ng)|dbte|dc-s|devi|dica|dmob|do(c|p)o|ds(12|-d)|el(49|ai)|em(l2|ul)|er(ic|k0)|esl8|ez([4-7]0|os|wa|ze)|fetc|fly(-|_)|g1 u|g560|gene|gf-5|g-mo|go(\.w|od)|gr(ad|un)|haie|hcit|hd-(m|p|t)|hei-|hi(pt|ta)|hp( i|ip)|hs-c|ht(c(-| |_|a|g|p|s|t)|tp)|hu(aw|tc)|i-(20|go|ma)|i230|iac( |-|\/)|ibro|idea|ig01|ikom|im1k|inno|ipaq|iris|ja(t|v)a|jbro|jemu|jigs|kddi|keji|kgt( |\/)|klon|kpt |kwc-|kyo(c|k)|le(no|xi)|lg( g|\/(k|l|u)|50|54|-[a-w])|libw|lynx|m1-w|m3ga|m50\/|ma(te|ui|xo)|mc(01|21|ca)|m-cr|me(rc|ri)|mi(o8|oa|ts)|mmef|mo(01|02|bi|de|do|t(-| |o|v)|zz)|mt(50|p1|v )|mwbp|mywa|n10[0-2]|n20[2-3]|n30(0|2)|n50(0|2|5)|n7(0(0|1)|10)|ne((c|m)-|on|tf|wf|wg|wt)|nok(6|i)|nzph|o2im|op(ti|wv)|oran|owg1|p800|pan(a|d|t)|pdxg|pg(13|-([1-8]|c))|phil|pire|pl(ay|uc)|pn-2|po(ck|rt|se)|prox|psio|pt-g|qa-a|qc(07|12|21|32|60|-[2-7]|i-)|qtek|r380|r600|raks|rim9|ro(ve|zo)|s55\/|sa(ge|ma|mm|ms|ny|va)|sc(01|h-|oo|p-)|sdk\/|se(c(-|0|1)|47|mc|nd|ri)|sgh-|shar|sie(-|m)|sk-0|sl(45|id)|sm(al|ar|b3|it|t5)|so(ft|ny)|sp(01|h-|v-|v )|sy(01|mb)|t2(18|50)|t6(00|10|18)|ta(gt|lk)|tcl-|tdg-|tel(i|m)|tim-|t-mo|to(pl|sh)|ts(70|m-|m3|m5)|tx-9|up(\.b|g1|si)|utst|v400|v750|veri|vi(rg|te)|vk(40|5[0-3]|-v)|vm40|voda|vulc|vx(52|53|60|61|70|80|81|83|85|98)|w3c(-| )|webc|whit|wi(g |nc|nw)|wmlb|wonu|x700|yas-|your|zeto|zte-/i.test(navigator.userAgent.substr(0,4))) { 
            isSmall = true;
        }

        return (
            <Edit undoable={false} title={<PostTitle />} {...this.props}>
                <TabbedForm style={{maxWidth: "100vw"}} redirect="edit" variant="standard" tabs={<TabbedFormTabs variant='scrollable' scrollButtons='auto' />} warnWhenUnsavedChanges validate={this.validateVoucherSelect} toolbar={<CustomToolbar isSmall={isSmall} />} >
                    <FormTab label={translate('custom.transactions')}>

                        <OldUidsList style={{"margin" : "10px"}} />

                        <ClientDetailsView />

                        <QuickActions />

                        <TransactionsList isSmall={isSmall} currency={this.state.currency} />

                    </FormTab>

                    <FormTab label={translate('custom.vouchers')}>
                        <ArrayInput source="vouchers">
                            <SimpleFormIterator disableReordering>
                                <SelectInput
                                    fullWidth 
                                    label={translate('custom.voucher')} 
                                    source="voucher_id" 
                                    choices={this.state.vouchers} 
                                    validate={required()}
                                />
                                <NumberInput
                                    fullWidth
                                    source="quantity" 
                                    label={translate('custom.quantity')} 
                                    validate={validatePositiveNumber}
                                    onWheel={(e) => e.target.blur()}
                                    sx={{
                                        '& input': { fontSize: '16px' }
                                    }}
                                />
                            </SimpleFormIterator>
                        </ArrayInput>
                    </FormTab>

                    <FormTab label={translate('custom.personal_info')}>
                        <Grid container style={{width: '100%'}} spacing={2}>
                            <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                                <h1 style={{fontSize: '22px', color: '#08607e', fontWeight: 'normal', marginBottom: '0px'}}>{translate('custom.personal_info').toUpperCase()}</h1>
                            </Grid>
                            <Grid item xs={12} sm={12} md={6} lg={4} xl={4}>
                                <TextInput fullWidth source="name" label={translate('custom.name')} autoFocus variant="outlined" />
                            </Grid>
                            <Grid item xs={12} sm={12} md={6} lg={4} xl={4}>
                                <SelectInput fullWidth label={translate('custom.age')} source="age" choices={getChoices(config.entrance_ages)} variant="outlined" />
                            </Grid>
                            <Grid item xs={12} sm={12} md={6} lg={4} xl={4}>
                                <TextInput fullWidth source="ticket_id" label={translate('custom.ticket_id')} disabled={true} variant="outlined" />
                            </Grid>
                            <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                                <h2 style={{fontSize: '18px', color: '#08607e', fontWeight: 'normal', marginBottom: '0px'}}>{translate('custom.address_info').toUpperCase()}</h2>
                            </Grid>
                            <Grid item xs={12} sm={12} md={8} lg={4} xl={4}>
                                <TextInput fullWidth source="street_name" label={translate('custom.street_name')} variant="outlined" />
                            </Grid>
                            <Grid item xs={12} sm={12} md={4} lg={2} xl={2}>
                                <TextInput fullWidth source="street_number" label={translate('custom.street_number')} variant="outlined" />
                            </Grid>
                            <Grid item xs={12} sm={12} md={4} lg={2} xl={2}>
                                <TextInput fullWidth source="postal_code" label={translate('custom.postal_code')} variant="outlined" />
                            </Grid>
                            <Grid item xs={12} sm={12} md={8} lg={4} xl={4}>
                                <TextInput fullWidth source="locality" label={translate('custom.locality')} variant="outlined" />
                            </Grid>
                            <Grid item xs={12} sm={12} md={6} lg={4} xl={4}>
                                <TextInput multiline fullWidth source="address_complement" label={translate('custom.address_complement')} variant="outlined" />
                            </Grid>
                            <Grid item xs={12} sm={12} md={6} lg={4} xl={4}>
                                <TextInput fullWidth source="email" label={translate('custom.email')} variant="outlined" />
                            </Grid>
                            <Grid item xs={12} sm={12} md={6} lg={4} xl={4}>
                                <TextInput fullWidth source="phone_number" label={translate('custom.phone_number')} variant="outlined" />
                            </Grid>
                            <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                                <h2 style={{fontSize: '18px', color: '#08607e', fontWeight: 'normal', marginBottom: '0px'}}>{translate('custom.bank_info').toUpperCase()}</h2>
                            </Grid>
                            <Grid item xs={12} sm={12} md={6} lg={4} xl={4}>
                                <TextInput fullWidth source="iban" label={translate('custom.iban')} variant="outlined" />
                            </Grid>
                            <Grid item xs={12} sm={12} md={6} lg={4} xl={4}>
                                <TextInput fullWidth source="bic" label={translate('custom.bic')} variant="outlined" />
                            </Grid>
                            <Grid item xs={12} sm={12} md={6} lg={4} xl={4}>
                                <TextInput fullWidth source="bank" label={translate('custom.bank') + ' / ' + translate('custom.post')} variant="outlined" />
                            </Grid>
                            <Grid item xs={12} sm={12} md={12} lg={8} xl={6}>
                                <TextInput fullWidth source="establishment_address" label={translate('custom.establishment_address')} variant="outlined" />
                            </Grid>
                            <Grid item xs={12} sm={12} md={12} lg={6} xl={6}>
                                <TextInput multiline fullWidth source="payment_info" label={translate('custom.payment_info')} variant="outlined" />
                            </Grid>
                        </Grid>
                        
                    </FormTab>
                </TabbedForm>
            </Edit>
        )

    }

}

const styles = {
    disable_button: {
        color: 'red',
        borderColor: 'red',
        marginLeft: 'auto',
        marginRight: '20px'
    },
    enable_button: {
        color: config.baseColor,
        borderColor: config.baseColor,
        marginLeft: 'auto',
        marginRight: '20px'
    }
}